import { useCallback, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { TimeSignal, TimeSignalClassification } from 'graphql/types';
import { useNotesSelectionReset } from 'modules/notes/list/hooks';
import { mapTimesheetLabel } from 'modules/notes/common/utils';
import {
  CustomLabel,
  FormTimesheetLabel
} from 'modules/timesheet-labels/labelSchema';
import { useFeatureFlag } from 'modules/common/hooks';
import { useMultiEditActions } from './useMultiEditActions';

export const useMultiEditBulkActionCallback = ({
  notes,
  getValues
}: {
  notes: TimeSignal[];
  getValues: UseFormReturn['getValues'];
}): {
  bulkActionCallback: (
    onNoteDiscard: () => void,
    classificationTarget: TimeSignalClassification
  ) => void;
  isActionInProgress: boolean;
} => {
  const [saving, setSaving] = useState(false);
  const { resetForm } = useNotesSelectionReset();
  const { handleMultiEditAction, isActionInProgress } = useMultiEditActions();
  const isTimeSignalFederatedSuggestionsEnabled = useFeatureFlag(
    'isTimeSignalFederatedSuggestionsEnabled',
    false
  );
  const bulkActionCallback = useCallback(
    async (
      onNoteDiscard: () => void,
      classificationTarget: TimeSignalClassification
    ) => {
      const { timesheetLabels, customLabels } = getValues();
      const timesheetLabelValues = (
        (timesheetLabels as FormTimesheetLabel[]) || []
      )
        .filter(({ label }: FormTimesheetLabel) => label.value)
        .map(({ label }: FormTimesheetLabel) =>
          mapTimesheetLabel(
            true,
            label,
            isTimeSignalFederatedSuggestionsEnabled
          )
        );

      const customLabelValues = ((customLabels as CustomLabel[]) || [])
        .filter((l) => l.value)
        .map((l) =>
          mapTimesheetLabel(true, l, isTimeSignalFederatedSuggestionsEnabled)
        );

      setSaving(true);

      await handleMultiEditAction({
        labelValues: timesheetLabelValues.concat(customLabelValues),
        selectedNotes: notes.map((note) => note.id),
        isAllSelected: false,
        unSelectedNotes: [],
        classificationTarget
      });
      onNoteDiscard();
      resetForm();
      setSaving(false);
    },
    [
      getValues,
      handleMultiEditAction,
      isTimeSignalFederatedSuggestionsEnabled,
      notes,
      resetForm
    ]
  );

  return {
    bulkActionCallback,
    isActionInProgress: isActionInProgress || saving
  };
};
